import React from 'react';
import { motion } from "framer-motion";

/**
 * Enhanced ToggleSwitch Component
 * Modern, beautiful toggle switch with smooth animations
 */
const ToggleSwitch = ({
    label,
    checked = false,
    onChange,
    className = "",
    labelClassName = "",
    switchClassName = "",
    disabled = false,
    size = "md" // sm, md, lg
}) => {
    const sizeClasses = {
        sm: { width: "w-10", height: "h-5", circle: "w-4 h-4", translate: 20 }, // 5 * 4px = 20px
        md: { width: "w-14", height: "h-7", circle: "w-6 h-6", translate: 28 }, // 7 * 4px = 28px
        lg: { width: "w-16", height: "h-8", circle: "w-7 h-7", translate: 32 }, // 8 * 4px = 32px
    };

    const sizes = sizeClasses[size];

    const handleToggle = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        // Simply call onChange - the parent component (SharedForm) already handles the toggle logic
        if (onChange) {
            onChange(e);
        }
    };

    return (
        <div className={`inline-flex items-center gap-3 ${className}`}>
            {label && (
                <span className={`text-sm font-medium text-gray-900 dark:text-gray-300 ${labelClassName}`}>
                    {label}
                </span>
            )}
            <motion.button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-disabled={disabled}
                onClick={handleToggle}
                disabled={disabled}
                whileHover={disabled ? {} : { scale: 1.02 }}
                whileTap={disabled ? {} : { scale: 0.98 }}
                className={`
                    relative outline-none focus:outline-none rounded-full
                    ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                `}
            >
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={onChange}
                    className="sr-only"
                    disabled={disabled}
                    tabIndex={-1}
                    aria-hidden="true"
                />
                <motion.div
                    className={`
                        ${sizes.width} ${sizes.height}
                        rounded-full relative
                        transition-all duration-300 ease-in-out
                        ${checked
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }
                        ${disabled ? 'opacity-50' : ''}
                        ${switchClassName}
                    `}
                    animate={{
                        backgroundColor: checked
                            ? 'rgba(59, 130, 246, 1)'
                            : disabled
                                ? 'rgba(209, 213, 219, 1)'
                                : 'rgba(209, 213, 219, 0.5)',
                    }}
                    transition={{ duration: 0.3 }}
                >
                    <motion.div
                        className={`
                            ${sizes.circle}
                            absolute top-0.5 left-0.5
                            bg-white rounded-full
                            shadow-lg
                            transition-all duration-300 ease-in-out
                        `}
                        animate={{
                            x: checked ? sizes.translate : 0,
                        }}
                        transition={{
                            type: "spring",
                            stiffness: 500,
                            damping: 30,
                        }}
                        whileHover={!disabled ? { scale: 1.1 } : {}}
                    />
                </motion.div>
            </motion.button>
        </div>
    );
};

export default ToggleSwitch;
